import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Model, Serializer } from "survey-core";
import { flushSync } from "react-dom";
import "survey-core/survey-core.css";
import { Survey } from "survey-react-ui";
import { useMutation, useQuery } from "@apollo/client";
import { Alert, LoadingOverlay, Paper } from "@mantine/core";
import { PageFooter } from "@/components/layout/PageFooter";
import { gql } from "@/__generated__";
import Navbar from "@/components/layout/Navbar/Navbar";
import { ReferPatientProgressTracker } from "@/components/ReferPatientProgressTracker";
import { StrengthenReferral } from "../StrengthenReferral/StrengthenReferral";
import { useFormContext } from "@/contexts/FormContext";
import { useSurveySchemaByCondition } from "@/hooks/useSurveySchemaByCondition";
import { claimableConsoleSurveyTheme } from "../StrengthenReferral/components/tabs/PatientSurveyTab/surveyTheme";
import styles from "../StrengthenReferral/components/tabs/PatientSurveyTab/survey.module.scss";
import { registerTipQuestion } from "../../../survey/models/tip";
import { registerSuggestionsQuestion } from "../../../survey/models/suggestions";
import { registerSampleStoryQuestion } from "../../../survey/models/sample-story";
import { registerPillMultiSelectButtonQuestion } from "../../../survey/models/pill-multi-select-button";

const GET_SURVEY_DATA_FOR_APPEAL = gql(`
  query GetSurveyDataForPatientSurveyPage($appealId: ID!) {
    getAppealById(appealId: $appealId) {
      appealId
      surveyJsAnswers
      status {
        referred
      }
      denial {
        condition
        medicationBrandName
        denialReason
      }
    }
  }
`);

const UPDATE_SURVEY_ANSWERS = gql(`
  mutation UpdateSurveyAnswers($appealId: ID!, $surveyJsAnswers: String!, $surveySchemaId: ID!) {
    updateSurveyJsAnswers(
      appealId: $appealId
      surveyJsAnswers: $surveyJsAnswers
      surveySchemaId: $surveySchemaId
    )
  }
`);

registerTipQuestion();
registerSuggestionsQuestion();
registerSampleStoryQuestion();
registerPillMultiSelectButtonQuestion();

export function PatientSurvey() {
  const { appealId } = useParams<{ appealId: string }>();
  const [survey, setSurvey] = useState<Model | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const { setIsDirty, resetDirty } = useFormContext();
  const [originalSurveyAnswers, setOriginalSurveyAnswers] = useState<
    string | null
  >(null);

  const { data, loading, error } = useQuery(GET_SURVEY_DATA_FOR_APPEAL, {
    variables: { appealId: appealId! },
    skip: !appealId,
    fetchPolicy: "no-cache",
  });

  const readOnly = true;

  const [updateSurveyJsAnswers] = useMutation(UPDATE_SURVEY_ANSWERS);

  const condition = data?.getAppealById?.denial?.condition || "";

  const {
    surveySchema,
    surveySchemaId,
    loading: schemaLoading,
    error: schemaError,
  } = useSurveySchemaByCondition(condition);

  useEffect(() => {
    Serializer.addProperty("boolean", {
      name: "renderAs",
      default: "radio",
    });
  }, []);

  useEffect(() => {
    if (data?.getAppealById && surveySchema && surveySchemaId) {
      const contextData = {
        "context.condition": condition,
        "context.medication": data.getAppealById.denial?.medicationBrandName,
        "context.denialReason": data.getAppealById.denial?.denialReason,
      };

      let initialData: Record<string, unknown> = contextData;

      if (data.getAppealById.surveyJsAnswers) {
        try {
          const existingAnswers = JSON.parse(
            data.getAppealById.surveyJsAnswers,
          );
          initialData = { ...contextData, ...existingAnswers };
        } catch (parseError) {
          console.error("Error parsing existing survey answers:", parseError);
        }
      }

      const filterQuestions = (elements: Array<{ type?: string }>) => {
        return elements.filter((element) => {
          const questionType = element.type;
          return (
            questionType !== "tip" &&
            questionType !== "samplestory" &&
            questionType !== "suggestions"
          );
        });
      };

      const schemaWithPages = surveySchema as {
        pages?: Array<{ elements?: Array<{ type?: string }> }>;
      };

      const filteredSchema = {
        ...surveySchema,
        pages: Array.isArray(schemaWithPages.pages)
          ? schemaWithPages.pages.map((page) => ({
              ...page,
              elements: page.elements ? filterQuestions(page.elements) : [],
            }))
          : [],
      };

      const newSurvey = new Model({
        ...filteredSchema,
        showNavigationButtons: "none",
        clearInvisibleValues: "onHiddenContainer",
        requiredText: "",
        questionsOnPageMode: "singlePage",
      });

      newSurvey.data = initialData;
      newSurvey.showCompletedPage = false;
      newSurvey.applyTheme(claimableConsoleSurveyTheme);
      newSurvey.css = {
        body: styles.body,
        page: {
          title: styles.pageTitle,
          description: styles.pageDescription,
        },
        checkbox: {
          root: styles.checkboxPillRoot,
          materialDecorator: styles.checkboxMaterialDecorator,
          itemChecked: styles.checkboxItemChecked,
        },
        actionBar: {
          root: styles.actionBarRoot,
        },
        comment: {
          root: styles.commentRoot,
        },
        boolean: {
          rootRadio: styles.booleanPillRoot,
          radioFieldset: styles.booleanPillRoot,
          radioItem: "sd-item sd-radio sd-selectbase__item",
          radioItemChecked: styles.booleanRadioItemChecked,
          radioLabel: "sd-selectbase__label",
          materialRadioDecorator: "sd-item__decorator sd-radio__decorator",
        },
        dropdown: {
          control: styles.dropdownControl,
          controlValue: styles.dropdownControlValue,
          filterStringInput: styles.dropdownFilterStringInput,
          other: styles.dropdownOther,
        },
        list: {
          root: styles.listRoot,
          itemSelected: styles.listItemSelected,
          itemFocused: styles.listItemFocused,
          item: styles.listItem,
          itemBody: styles.listItemBody,
        },
        radiogroup: {
          root: styles.radiogroupPillRoot,
          itemChecked: styles.radiogroupItemChecked,
        },
        text: {
          root: styles.textRoot,
        },
        tagbox: {
          selectWrapper: styles.tagboxSelectWrapper,
          control: styles.tagboxControl,
          filterStringInput: styles.tagboxFilterStringInput,
          controlValue: styles.tagboxControlValue,
          cleanItemButton: styles.tagboxCleanItemButton,
          cleanItemButtonSvg: styles.tagboxCleanItemButtonSvg,
          hintSuffix: styles.tagboxHintSuffix,
          popup: styles.tagboxPopup,
        },
        paneldynamic: {
          mainRoot: styles.paneldynamicMainRoot,
          buttonAdd: styles.paneldynamicButtonAdd,
          buttonRemove: styles.paneldynamicButtonRemove,
        },
      };

      if (readOnly) {
        newSurvey.mode = "display";
      } else {
        newSurvey.mode = "edit";
        setOriginalSurveyAnswers(JSON.stringify(initialData));
      }

      setSurvey(newSurvey);
    }
  }, [
    data,
    surveySchema,
    surveySchemaId,
    appealId,
    condition,
    readOnly,
    updateSurveyJsAnswers,
    setIsDirty,
  ]);

  useEffect(() => {
    if (survey) {
      survey.mode = readOnly ? "display" : "edit";
    }
  }, [survey, readOnly]);

  useEffect(() => {
    if (!survey || readOnly || !originalSurveyAnswers) return;

    survey.onValueChanged.add(() => {
      const currentData = JSON.stringify(survey.data);
      const isDirty = currentData !== originalSurveyAnswers;
      setIsDirty(isDirty);
    });
  }, [survey, originalSurveyAnswers, setIsDirty, readOnly]);

  useEffect(() => {
    return () => {
      setIsDirty(false);
    };
  }, [setIsDirty]);

  if (!appealId) {
    return null;
  }

  const handleSave = async () => {
    if (!survey || !surveySchemaId) return;

    setIsSaving(true);
    const surveyJsAnswers = JSON.stringify(survey.data);
    try {
      await updateSurveyJsAnswers({
        variables: {
          appealId,
          surveyJsAnswers,
          surveySchemaId,
        },
      });

      setOriginalSurveyAnswers(surveyJsAnswers);
      flushSync(() => {
        resetDirty();
      });
    } catch (saveError) {
      console.error("Error saving survey answers:", saveError);
    } finally {
      setIsSaving(false);
    }
  };

  const footer = !readOnly ? (
    <PageFooter
      primaryButton={{
        text: "Save",
        onClick: handleSave,
        loading: isSaving,
        disabled: isSaving,
      }}
    />
  ) : undefined;

  if (loading || schemaLoading) {
    return (
      <Navbar
        titleComponent={<ReferPatientProgressTracker appealId={appealId} />}
        footer={footer}
      >
        <StrengthenReferral
          appealId={appealId}
          activeTab="patient-survey"
          tabContent={
            <Paper p="xl" shadow="sm" withBorder>
              <LoadingOverlay visible />
              <div style={{ minHeight: "200px" }} />
            </Paper>
          }
        />
      </Navbar>
    );
  }

  if (error || schemaError) {
    return (
      <Navbar
        titleComponent={<ReferPatientProgressTracker appealId={appealId} />}
        footer={footer}
      >
        <StrengthenReferral
          appealId={appealId}
          activeTab="patient-survey"
          tabContent={
            <Paper p="xl" shadow="sm" withBorder>
              <Alert color="red" title="Error loading survey">
                {schemaError
                  ? "No survey found for this condition. Please ensure the condition is properly configured."
                  : "Unable to load survey data. Please try again."}
              </Alert>
            </Paper>
          }
        />
      </Navbar>
    );
  }

  if (!survey) {
    return (
      <Navbar
        titleComponent={<ReferPatientProgressTracker appealId={appealId} />}
        footer={footer}
      >
        <StrengthenReferral
          appealId={appealId}
          activeTab="patient-survey"
          tabContent={
            <Paper p="xl" shadow="sm" withBorder>
              <Alert color="blue" title="No survey available">
                No patient survey is currently available for this appeal.
              </Alert>
            </Paper>
          }
        />
      </Navbar>
    );
  }

  return (
    <Navbar
      titleComponent={<ReferPatientProgressTracker appealId={appealId} />}
      footer={footer}
    >
      <StrengthenReferral
        appealId={appealId}
        activeTab="patient-survey"
        tabContent={
          <Paper className={styles.container} p="xl" shadow="sm" withBorder>
            <Survey model={survey} />
          </Paper>
        }
      />
    </Navbar>
  );
}
